import React, { useCallback, useEffect, useState } from "react";
import { BellRing, CalendarX, Check, MessageCircle, Phone, RefreshCw } from "lucide-react";
import { Groupe, TitreGroupe, Vide } from "../espace/ui";
import { api, erreurDe, chf, jourLisible, tel, sms } from "./commun";
import CalendrierPrives from "../components/CalendrierPrives";
import { duree as dureeTexte, franc, grilleTexte, moinsDe, pourRienTexte, regleSalaire, regles as reglesDe } from "../lib/reglesPrives";

/* Onglet « Privés » (Lydia, Miller, Noémie ; école, 30.10.2026) : les cours privés réservés
   sur le site, puis un seul « Calendrier » (Jen, 31.10.2026 : « mon calendrier et la
   disponibilité des profs, tout dans le calendrier ») — tous les profs (une seule salle ; sans
   les contacts des familles), et « Modifier mes disponibilités » : ce jour seulement ou tous
   les jeudis (« dispo les jeudis à 17h » une fois pour toutes). La réservation fait foi : en
   cas de changement, le prof contacte directement la personne.
   Un cours que la famille a déplacé dit son ancien horaire ; un cours annulé (par la famille ou
   par l'école) passe dans « Annulés » au lieu de disparaître sans un mot — en plus de l'e-mail
   au prof, avec l'agenda à jour (Jen, 02.11.2026 : « le prof est informé non ? sur app et par
   email ? »).
   Jen, 02.11.2026 : l'école peut tout modifier (« Modifier… », console et app école) — le cours
   dit alors son ancien horaire, son ancienne durée ou le prof qui l'avait (« Confié par
   l'école ») ; celui qui le perd le voit dans « Annulés » (« Confié à Noémie par l'école »).
   Un cours du jour déjà passé quitte « Cours réservés » pour « Aujourd'hui · passés », avec
   « Absent·e » quand l'école a noté l'absence (série de tests du 02.11.2026 : il restait parmi
   les cours réservés, sans la mention).
   Son salaire (Jen, 02.11.2026 : « payé cash par l'école … on les sort des décomptes mensuels » ;
   le soir : « 30.– pour 30 minutes, 40.– pour 45 minutes, 50.– pour 60 minutes, +5.– pour le
   déplacement ») : sur chaque cours, et ce qu'il recevra de l'école ce mois-ci (et le mois passé)
   — à titre d'information, hors de l'onglet Heures. Déplacé pour rien (famille absente,
   annulation à moins d'1 h créditée par l'école, annulation de l'école « prof déplacé ») : 50 %
   du salaire + le déplacement. Grille, pourcentage et seuil : Réglages › Cours privés. Noémie
   et Miller, propriétaires, n'ont pas ce bloc (`salaires` vide). */
export default function Prives() {
  const [a, setA] = useState(null);
  const [erreur, setErreur] = useState("");
  // « C'est enregistré » : une bulle en bas de l'écran, quelques secondes (la fenêtre se ferme
  // sur le calendrier ou la semaine type : un message en haut ne se voyait pas).
  const [message, setMessage] = useState("");
  useEffect(() => { if (!message) return undefined; const t = setTimeout(() => setMessage(""), 3500); return () => clearTimeout(t); }, [message]);
  const [cal, setCal] = useState(null);
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7));
  const chargerCal = useCallback(() => {
    api.get("/cours-prives/calendrier", { params: { du: `${mois}-01`, jours: 42 } }).then(({ data }) => setCal(data)).catch(() => setCal(null));
  }, [mois]);
  useEffect(() => { chargerCal(); }, [chargerCal]);
  useEffect(() => {
    api.get("/cours-prives", { params: { jours: 14 } }).then(({ data }) => setA(data)).catch((e) => setErreur(erreurDe(e)));
  }, []);
  // Mes disponibilités : ce jour seulement, tous les jeudis (?toutes=true), ou retour à la
  // semaine type (plages = null).
  const enregistrer = async (pid, jour, plages, toutes) => {
    setMessage("");
    try {
      const { data } = plages === null ? await api.delete(`/cours-prives/jour/${jour}`)
        : await api.put(`/cours-prives/jour/${jour}`, { plages }, { params: toutes ? { toutes: true } : {} });
      setA(data);
      chargerCal();
      // La fenêtre se ferme : on dit que c'est fait (comme la console et l'app école).
      setMessage(toutes ? "C'est enregistré : ta semaine type a changé." : plages === null
        ? "C'est enregistré : ce jour reprend ta semaine type." : "C'est enregistré pour ce jour-là.");
    } catch (e) { throw new Error(erreurDe(e)); }
  };
  if (erreur) return <Vide>{erreur}</Vide>;
  if (!a) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  // L'heure du cours est passée (aujourd'hui) : il quitte « Cours réservés ».
  const commence = (r) => new Date(`${r.date}T${r.debut}:00`).getTime() <= Date.now();
  const reservees = (a.a_venir || []).filter((r) => !commence(r));
  const passesDuJour = (a.a_venir || []).filter(commence);
  const annules = a.annules || [];
  const salaires = a.salaires || null;
  const regle = regleSalaire(salaires?.regle);
  const salaire = (r) => (r.salaire_prof === null || r.salaire_prof === undefined ? "" : `ton salaire : ${chf(r.salaire_prof)}`);
  // « le 02.11 » : le jour où la famille (ou l'école) a fait le changement.
  const le = (instant) => {
    const d = new Date(instant || "");
    return Number.isNaN(d.getTime()) ? "" : ` le ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  // « Mardi 3 novembre » (la classe « capitalize » écrivait « Novembre »).
  const jour = (iso) => { const t = jourLisible(iso); return t.charAt(0).toUpperCase() + t.slice(1); };
  return (
    <div className="space-y-6" data-testid="prof-prives">
      {a.rappel && (
        <div className="rounded-[22px] px-4 py-3 text-[14px] flex gap-2" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="prof-prives-rappel">
          <BellRing size={18} className="shrink-0 mt-0.5" />
          <span>Aucune plage ces deux prochaines semaines : personne ne peut réserver de cours privé avec toi. Ajoute-en dans le calendrier : « Modifier mes disponibilités ».</span>
        </div>
      )}
      {/* Ce que l'école lui remet, en cash : ce mois-ci (dont à venir) et le mois passé. */}
      {salaires && (salaires.mois.nb > 0 || salaires.mois_passe.nb > 0) && (
        <section data-testid="prof-prives-salaire">
          <TitreGroupe>Salaire des cours privés</TitreGroupe>
          <Groupe>
            {[salaires.mois, salaires.mois_passe].filter((m) => m.nb > 0).map((m) => (
              <div key={m.mois} className="flex items-center gap-3 px-4 py-3" data-testid={m === salaires.mois ? "prof-prives-salaire-mois" : "prof-prives-salaire-passe"}>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-snug">
                    {m === salaires.mois ? "À recevoir de l'école ce mois-ci" : `En ${m.mois_label.split(" ")[0]}`}
                  </div>
                  <div className="text-[12px] text-[var(--muted)]">
                    {m.mois_label.replace(/^./, (c) => c.toUpperCase())} · {m.nb} cours{m.nb_a_venir > 0 ? ` (dont ${m.nb_a_venir} à venir)` : ""}{m.a_confirmer > 0 ? ` · ${m.a_confirmer} à confirmer par l'école` : ""}
                  </div>
                </div>
                <div className="text-[17px] font-semibold tabular-nums shrink-0">{chf(m.total)}</div>
              </div>
            ))}
          </Groupe>
          <p className="text-[12px] text-[var(--muted)] px-1 mt-1.5" data-testid="prof-prives-salaire-regle">
            Payé en cash par l'école, hors de tes heures du mois. {grilleTexte(regle)}, + {franc(regle.deplacement)} de déplacement.
            {" "}Déplacé·e pour rien (famille absente, ou annulation {moinsDe(dureeTexte(reglesDe(a.regles).deplacement_minutes))} avant que l'école te crédite) : {pourRienTexte(regle)}.
          </p>
        </section>
      )}
      <section>
        <TitreGroupe>Cours réservés · {reservees.length}</TitreGroupe>
        {!reservees.length ? <Vide>Aucun cours privé réservé pour l'instant.</Vide> : (
          <Groupe>
            {reservees.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid="prof-prive">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-[var(--muted)]">{jour(r.date)} · {r.debut}–{r.fin}</div>
                  <div className="text-[15px] font-semibold leading-snug">{r.discipline_label} · {r.eleve_prenom} {r.eleve_nom}</div>
                  <div className="text-[12px] text-[var(--muted)]">{r.minutes} min · {chf(r.montant)} payés sur place{salaire(r) ? ` · ${salaire(r)}` : ""}{r.objectif ? ` · « ${r.objectif} »` : ""}</div>
                  {(r.ancien_quand || r.ancien_minutes > 0) && (
                    <div className="text-[12px] font-medium mt-0.5" style={{ color: "#B45309" }} data-testid="prof-prive-deplace">
                      {r.ancien_quand ? "Nouvel horaire" : "Modifié par l'école"}{le(r.ancien_quand ? r.deplacee_le : r.modifiee_le)} · avant : {[r.ancien_quand, r.ancien_minutes > 0 ? `${r.ancien_minutes} min` : ""].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {r.repris_de && (
                    <div className="text-[12px] font-medium mt-0.5" style={{ color: "#0E7490" }} data-testid="prof-prive-confie">
                      Confié par l'école{le(r.modifiee_le)} · prévu avec {r.repris_de}
                    </div>
                  )}
                </div>
                <a href={sms(r.telephone)} aria-label="Écrire" className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ border: "1px solid var(--border)" }}><MessageCircle size={16} /></a>
                <a href={tel(r.telephone)} aria-label="Appeler" className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ border: "1px solid var(--border)" }}><Phone size={16} /></a>
              </div>
            ))}
          </Groupe>
        )}
      </section>
      {passesDuJour.length > 0 && (
        <section data-testid="prof-prives-passes">
          <TitreGroupe>Aujourd'hui · passés</TitreGroupe>
          <Groupe>
            {passesDuJour.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid="prof-prive-passe">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-[var(--muted)]">{r.debut}–{r.fin}</div>
                  <div className="text-[15px] font-semibold leading-snug">{r.discipline_label} · {r.eleve_prenom} {r.eleve_nom}</div>
                  {r.absent ? (
                    <div className="text-[12px] font-medium mt-0.5" style={{ color: "#B45309" }} data-testid="prof-prive-absent">
                      Absent·e · la famille est facturée ; ce cours t'est payé{salaire(r) ? ` (${salaire(r)})` : ""}
                    </div>
                  ) : <div className="text-[12px] text-[var(--muted)]">{r.minutes} min{salaire(r) ? ` · ${salaire(r)}` : ""}</div>}
                </div>
              </div>
            ))}
          </Groupe>
        </section>
      )}
      {annules.length > 0 && (
        <section data-testid="prof-prives-annules">
          <TitreGroupe>Annulés · {annules.length}</TitreGroupe>
          <Groupe>
            {annules.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid="prof-prive-annule">
                <CalendarX size={18} className="shrink-0" style={{ color: "#B91C1C" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-[var(--muted)] line-through">{jour(r.date)} · {r.debut}–{r.fin}</div>
                  <div className="text-[15px] font-semibold leading-snug">{r.discipline_label} · {r.eleve_prenom} {r.eleve_nom}</div>
                  {r.confie_a ? (
                    <div className="text-[12px]" style={{ color: "#B91C1C" }} data-testid="prof-prive-confie-a">
                      Confié à {r.confie_a} par l'école{le(r.annulee_le)}
                    </div>
                  ) : (
                    <div className="text-[12px]" style={{ color: "#B91C1C" }}>
                      Annulé {r.annule_par === "famille" ? "par la famille" : "par l'école"}{le(r.annulee_le)}
                      {r.prof_paye ? ` · payé (${salaire(r) || "par l'école"})` : r.paie_a_confirmer ? " · l'école te confirme s'il t'est payé"
                        : r.paie_decidee ? " · pas payé (décision de l'école)" : ""}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Groupe>
        </section>
      )}
      <section data-testid="prof-prives-calendrier">
        <TitreGroupe>Calendrier</TitreGroupe>
        {message && (
          <div role="status" className="fixed inset-x-4 z-[70] mx-auto max-w-md rounded-2xl px-4 py-3 text-[14px] font-medium shadow-lg flex items-center gap-2"
            style={{ bottom: "calc(96px + env(safe-area-inset-bottom, 0px))", background: "#0F172A", color: "#fff" }} data-testid="prof-prives-message">
            <Check size={16} className="shrink-0" /> {message}
          </div>
        )}
        {cal ? <CalendrierPrives cal={cal} mois={mois} onMois={setMois} editables={[a.prof_id]} onEnregistrer={enregistrer} />
          : <Vide>Chargement…</Vide>}
      </section>
    </div>
  );
}
